import { useEffect, useRef } from "react";

/**
 * ConfettiBurst - a lightweight, dependency-free canvas confetti burst.
 * Fires a single short burst on mount (~1.6s) and then cleans itself up.
 * Purely decorative: rendered as a non-interactive overlay so it never
 * blocks the text or buttons underneath.
 */
export default function ConfettiBurst({ durationMs = 1600, count = 90 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Respect reduced-motion preferences.
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const colors = ["#F4772E", "#0D1B33", "#F5F0E1", "#F9A03F", "#ffffff", "#2E7D6B"];
    const G = 0.18; // gravity

    // Burst originates from the top-center and spreads outward + downward.
    const originX = width / 2;
    const originY = height * 0.18;

    const particles = Array.from({ length: count }).map(() => {
      const angle = (-Math.PI / 2) + (Math.random() - 0.5) * Math.PI * 1.1;
      const speed = 3 + Math.random() * 6;
      return {
        x: originX + (Math.random() - 0.5) * 40,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - Math.random() * 2,
        size: 5 + Math.random() * 6,
        color: colors[(Math.random() * colors.length) | 0],
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        shape: Math.random() > 0.5 ? "rect" : "circle",
      };
    });

    const start = performance.now();
    let raf;

    const tick = (now) => {
      const elapsed = now - start;
      const life = Math.min(elapsed / durationMs, 1);
      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.vy += G;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;

        ctx.save();
        ctx.globalAlpha = 1 - life; // fade out over the burst
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      });

      if (elapsed < durationMs) {
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durationMs, count]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="confetti-burst"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 w-full h-full"
    />
  );
}
